import { FC } from 'react';

import { SidebarIconBase, SidebarIconProps } from './SidebarIcon';

/**
 * Zestaw ikon line-art dla sidebara — zdestylowane wersje animowanych ilustracji
 * pustych stanów (`TableEmptyState`). Każda ikona dziedziczy kolor stanu przez
 * `currentColor` (biel na ciemnym teal tle menu) i jest „drop-in" zgodna z
 * ikonami `lucide-react` używanymi w `menuConfig`.
 *
 * Animacja po najechaniu: wybrany element-sygnatura każdej ikony jest oznaczony
 * klasą (`sb-draw`, `sb-spin`, `sb-pop`, `sb-float`, `sb-swing`, `sb-wave`,
 * `sb-bounce`, `sb-bars`, `sb-blink`). Reguły `:hover` i keyframes żyją w
 * `Sidebar.styled.ts` (wstrzyknięte w wiersz menu), więc animacja odpala się po
 * najechaniu na CAŁY element menu, nie tylko na samą ikonę. Wszystko gaśnie przy
 * `prefers-reduced-motion`.
 */

// Wspólny atrybut dla wypełnionych akcentów (kropki, dni, oczka).
const dot = { fill: 'currentColor', stroke: 'none' } as const;

// Ścieżka 5-ramiennej gwiazdy (środek cx,cy; promień zewn. R, wewn. 0.4R).
const star = (cx: number, cy: number, r: number): string => {
  let d = '';
  for (let i = 0; i < 10; i += 1) {
    const rad = i % 2 === 0 ? r : r * 0.4;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    d += `${i === 0 ? 'M' : 'L'}${(cx + rad * Math.cos(a)).toFixed(2)} ${(cy + rad * Math.sin(a)).toFixed(2)} `;
  }
  return `${d}Z`;
};

// 5 gwiazdek (rating hotelu) w układzie 3 + 2 — duże, ze staggerowanym „migotaniem".
const HOTEL_STARS = [
  { cx: 5, cy: 7.8 }, { cx: 12, cy: 7.8 }, { cx: 19, cy: 7.8 },
  { cx: 8.5, cy: 15.8 }, { cx: 15.5, cy: 15.8 },
].map((p, i) => ({
  d: star(p.cx, p.cy, 3.4),
  cls: i === 0 ? 'sb-pop' : `sb-pop sb-pop-${i + 1}`,
}));

// Dziennik aktywności — linia pulsu (rysuje się) + „żywy" punkt (pulsuje) nad
// wierszami dziennika. Wzór: ActivityIllustration.
export const ActivityJournalIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path className="sb-draw" d="M2 7.5 H7.5 L9.25 4 L11.75 11 L13.5 7.5 H19.5" />
    <circle className="sb-pop" cx={20.5} cy={7.5} r={1.4} {...dot} />
    <circle cx={3.6} cy={14.5} r={1.25} {...dot} />
    <path d="M7.5 14.5 H21" />
    <circle cx={3.6} cy={19} r={1.25} {...dot} />
    <path d="M7.5 19 H17" />
  </SidebarIconBase>
);

// Dashboard — tarcza wskaźnika (gauge) otwarta u dołu; wskazówka „omiata" po hover.
export const DashboardIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M6.8 17.8 A7 7 0 1 1 17.2 17.8" />
    <path d="M6.8 17.8 l1.3 -0.95" />
    <path d="M17.2 17.8 l-1.3 -0.95" />
    <g className="sb-sweep">
      <rect x={5} y={6} width={14} height={14} fill="none" stroke="none" />
      <path d="M12 13 L15.6 9.3" />
    </g>
    <circle cx={12} cy={13} r={1.4} {...dot} />
  </SidebarIconBase>
);

// Mój Hotel — rating 5 gwiazdek (★★★★★); gwiazdki migoczą kolejno po hover.
export const HotelIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    {HOTEL_STARS.map((s) => (
      <path key={s.d} className={s.cls} d={s.d} {...dot} />
    ))}
  </SidebarIconBase>
);

// Zgłoszenia — podkładka (clipboard) z wpisami i „rysującym się" ptaszkiem.
export const ReportsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={5} y={5} width={14} height={16} rx={2} />
    <rect x={9} y={3} width={6} height={3} rx={1} />
    <path d="M8.5 10 H15.5" />
    <path d="M8.5 13 H13.5" />
    <path className="sb-draw" d="M8.5 16.6 L11 19 L15.5 14" />
  </SidebarIconBase>
);

// G-Commerce — paragon z ząbkowanym dołem (delikatnie się unosi).
export const CommerceIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-float">
      <path d="M6 6 a1 1 0 0 1 1 -1 H17 a1 1 0 0 1 1 1 V19 l-2 -1.3 l-2 1.3 l-2 -1.3 l-2 1.3 l-2 -1.3 l-2 1.3 V6 Z" />
      <path d="M9 8.5 H15" />
      <path d="M9 11.5 H15" />
      <path d="M9 14.5 H13" />
    </g>
  </SidebarIconBase>
);

// Wydarzenia — kalendarz z wyróżnionym, pulsującym dniem.
export const EventsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={4} y={6} width={16} height={15} rx={2} />
    <path d="M4 10 H20" />
    <path d="M8.5 4 V7" />
    <path d="M15.5 4 V7" />
    <circle cx={8.5} cy={14} r={1} {...dot} />
    <circle cx={15.5} cy={14} r={1} {...dot} />
    <circle cx={8.5} cy={17.5} r={1} {...dot} />
    <circle cx={15.5} cy={17.5} r={1} {...dot} />
    <circle className="sb-pop" cx={12} cy={14} r={1.8} {...dot} />
  </SidebarIconBase>
);

// Konferencje — ekran/plansza na stojaku z rysującą się linią trendu.
export const ConferencesIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={4} y={4} width={16} height={11} rx={1.5} />
    <path d="M12 15 V18" />
    <path d="M8.5 20.5 L12 18 L15.5 20.5" />
    <path className="sb-draw" d="M7.5 11.5 L10.5 8.5 L13 10.5 L16.5 6.5" />
    <circle cx={16.5} cy={6.5} r={1.1} {...dot} />
  </SidebarIconBase>
);

// Oferty Specjalne — prezent z pulsującą kokardą.
export const OffersIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={5} y={10.5} width={14} height={10.5} rx={1} />
    <rect x={4} y={7} width={16} height={3.8} rx={1} />
    <path d="M12 7 V21" />
    <g className="sb-pop">
      <path d="M12 7 C9 3.5 5.5 5.5 9 7" />
      <path d="M12 7 C15 3.5 18.5 5.5 15 7" />
      <circle cx={12} cy={7} r={1.1} {...dot} />
    </g>
  </SidebarIconBase>
);

// Marketing — megafon z rozchodzącymi się falami dźwięku.
export const MarketingIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M5 10 L14 6.5 V15.5 L5 12 Z" />
    <path d="M8 12.2 V16 a1.6 1.6 0 0 0 3.2 0 V13.4" />
    <path className="sb-wave" d="M16 8.5 a4.5 4.5 0 0 1 0 5.5" />
    <path className="sb-wave sb-wave-2" d="M18.5 6.8 a8 8 0 0 1 0 9" />
  </SidebarIconBase>
);

// Usługi / Outlets — klosz room service (kopuła unosi się nad tacą).
export const ServicesIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M5 17.5 H19" />
    <g className="sb-float">
      <path d="M6.5 17 A5.6 7 0 0 1 17.5 17 Z" />
      <path d="M12 10 V7.6" />
      <circle cx={12} cy={6.8} r={1.2} {...dot} />
    </g>
  </SidebarIconBase>
);

// AI Agents — robot-asystent (mruga oczami), z anteną i diodą.
export const AiAgentIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M12 5.5 V3" />
    <circle cx={12} cy={2.4} r={1.3} {...dot} />
    <rect x={5} y={5.5} width={14} height={12} rx={4} />
    <g className="sb-blink">
      <circle cx={9.5} cy={11} r={1.5} {...dot} />
      <circle cx={14.5} cy={11} r={1.5} {...dot} />
    </g>
    <path d="M9.5 14 Q12 15.6 14.5 14" />
    <path d="M9 17.5 V19.5" />
    <path d="M15 17.5 V19.5" />
  </SidebarIconBase>
);

// Widgety — wyważony puzzel (knob u góry i po prawej); kiwa się po hover.
export const WidgetsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-swing">
      <path d="M5 8.5 a1.2 1.2 0 0 1 1.2 -1.2 H8.8 a2.6 2.6 0 1 1 4.4 0 H16 a1.2 1.2 0 0 1 1.2 1.2 V11.2 a2.6 2.6 0 1 1 0 4.4 V18 a1.2 1.2 0 0 1 -1.2 1.2 H6.2 a1.2 1.2 0 0 1 -1.2 -1.2 Z" />
    </g>
  </SidebarIconBase>
);

// Ankiety — dymek rozmowy z „piszącymi" kropkami (skaczą).
export const SurveysIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={4} y={5} width={16} height={11} rx={3} />
    <path d="M9 16 V20 L13 16" />
    <circle className="sb-bounce" cx={9} cy={10.5} r={1.3} {...dot} />
    <circle className="sb-bounce sb-bounce-2" cx={12} cy={10.5} r={1.3} {...dot} />
    <circle className="sb-bounce sb-bounce-3" cx={15} cy={10.5} r={1.3} {...dot} />
  </SidebarIconBase>
);

// Analityka — słupki wykresu (rosną od podstawy) z osią.
export const AnalyticsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M5 3 V19 H21" />
    <path className="sb-bars" d="M9 18.5 V13" strokeWidth={2.4} />
    <path className="sb-bars sb-bars-2" d="M13 18.5 V9.5" strokeWidth={2.4} />
    <path className="sb-bars sb-bars-3" d="M17 18.5 V6" strokeWidth={2.4} />
  </SidebarIconBase>
);

// Analiza Sprzedaży — osie wykresu z rosnącą linią trendu (rysuje się po
// najechaniu) i monetą sprzedaży w rogu.
export const SalesAnalyticsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path d="M4 4 V20 H20" />
    <path className="sb-draw" d="M7 15 L11 11 L13.5 13.5 L18 8" />
    <path className="sb-draw" d="M18 8 H14.5 M18 8 V11.5" />
    <circle cx={16.5} cy={17} r={2.3} {...dot} />
  </SidebarIconBase>
);

// Zwroty środków — strzałka zawracająca nad banknotem: pieniądze wracające do gościa.
// Strzałka „rysuje się" po najechaniu, więc ruch idzie w stronę zwrotu, nie sprzedaży.
export const ReturnsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <path className="sb-draw" d="M4 9 A7 7 0 0 1 18 9" />
    <path className="sb-draw" d="M4 5 V9 H8" />
    <rect x={3.5} y={12.5} width={17} height={7.5} rx={2} />
    <circle cx={12} cy={16.25} r={1.8} {...dot} />
  </SidebarIconBase>
);

// Generator QR — kod QR (trzy rogi pozycjonujące + moduły); akcentowy róg „pyka" po hover.
export const QrIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <rect x={3.5} y={3.5} width={6} height={6} rx={1.5} />
    <rect className="sb-pop" x={14.5} y={3.5} width={6} height={6} rx={1.5} />
    <rect x={3.5} y={14.5} width={6} height={6} rx={1.5} />
    <path d="M14.5 14.5 H17 M20.5 14.5 V17 M14.5 18 V20.5 M18 20.5 H20.5 M18 17 H20.5" />
  </SidebarIconBase>
);

// Biblioteka mediów — ramka zdjęcia (góry + słońce) z podpowiedzią stosu zdjęć
// z tyłu; słońce „pyka" po najechaniu. Wzór: ikona „images".
export const MediaLibraryIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    {/* podpowiedź drugiego zdjęcia za spodem (stos) */}
    <path d="M6.5 4.5 H19.5 A1.5 1.5 0 0 1 21 6 V16.5" />
    {/* przednia ramka zdjęcia */}
    <rect x={3.5} y={6.5} width={14} height={14} rx={2} />
    {/* góry */}
    <path d="M3.5 17.5 L8.5 13 L12 16.5" />
    <path d="M10 18.5 L13.5 15 L17.5 19" />
    {/* słońce — „pyka" po hover */}
    <circle className="sb-pop" cx={8} cy={11} r={1.7} {...dot} />
  </SidebarIconBase>
);

// Opera Cloud (PMS Oracle) — pełna czerwona kula w barwie marki (#c74634) z białym
// znakiem „O" z logo OPERA Cloud. Celowo nie dziedziczy `currentColor` (jak reszta ikon),
// bo to znak rozpoznawczy integracji — kolor ma zostać ten sam w każdym stanie menu.
export const OperaCloudIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <circle cx={12} cy={12} r={11} fill="#c74634" stroke="none" />
    {/* znak OPERA Cloud — białe „O" w formie grubego pierścienia-stadionu
        (rx = połowa wysokości, więc końce są w pełni zaokrąglone) */}
    <rect
      x={6}
      y={8.4}
      width={12}
      height={7.2}
      rx={3.6}
      fill="none"
      stroke="#ffffff"
      strokeWidth={3}
    />
  </SidebarIconBase>
);

// Ustawienia — trybik (pierścień zębatki + piasta), obraca się po najechaniu.
export const SettingsIcon: FC<SidebarIconProps> = (props) => (
  <SidebarIconBase {...props}>
    <g className="sb-spin">
      <rect x={2} y={2} width={20} height={20} fill="none" stroke="none" />
      <circle cx={12} cy={12} r={5.4} />
      <circle cx={12} cy={12} r={2} />
      <path d="M12 4 V6.6" />
      <path d="M12 17.4 V20" />
      <path d="M4 12 H6.6" />
      <path d="M17.4 12 H20" />
      <path d="M6.3 6.3 L8.1 8.1" />
      <path d="M15.9 15.9 L17.7 17.7" />
      <path d="M17.7 6.3 L15.9 8.1" />
      <path d="M6.3 17.7 L8.1 15.9" />
    </g>
  </SidebarIconBase>
);
